import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadIcon, DownloadIcon, CheckCircleIcon, WarningCircleIcon, XCircleIcon } from "@phosphor-icons/react";

/**
 * Reusable bulk-import dialog for trucks / trailers / drivers.
 *  props:
 *    open, onClose, onSuccess
 *    kind: "trucks" | "trailers" | "drivers"
 *    title, columnList (string) shown to user as a hint
 */
export default function BulkImportDialog({ open, onClose, onSuccess, kind, title, columnList }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };
  const close = () => { reset(); onClose(); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/imports/${kind}/template`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}_bulk_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const submit = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/imports/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} ${kind}`);
        onSuccess && onSuccess();
      } else {
        toast.info("No new records imported — see details below");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid={`bulk-import-${kind}`}>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Bulk import {title || kind}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border border-slate-200 rounded-sm bg-slate-50 p-3">
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 font-bold mb-1">Step 1 · Get the template</div>
            <div className="text-xs text-slate-600 mb-2">Columns: <span className="font-mono">{columnList}</span></div>
            <Button
              type="button"
              variant="outline"
              onClick={downloadTemplate}
              className="rounded-sm text-xs h-8"
              data-testid={`bulk-template-${kind}`}
            >
              <DownloadIcon size={14} weight="bold" className="mr-1.5" /> Download Excel template
            </Button>
          </div>

          <div className="border border-slate-200 rounded-sm p-3">
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 font-bold mb-1">Step 2 · Upload filled file</div>
            <label className="mt-1 flex items-center gap-2 border border-dashed border-slate-300 rounded-sm px-3 py-4 cursor-pointer hover:bg-slate-50">
              <UploadIcon size={18} weight="bold" className="text-blue-600" />
              <span className="text-sm text-slate-700 flex-1 truncate">{file ? file.name : "Choose .xlsx or .csv"}</span>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid={`bulk-file-${kind}`}
              />
            </label>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Duplicates (matching unit# or VIN) will be skipped and reported.
              {kind === "drivers" && (
                <span className="block mt-1 text-blue-700">
                  🔑 A login is auto-created for every driver row with an EMAIL. System-generated passwords are shown after import and can also be downloaded as CSV.
                </span>
              )}
            </p>
          </div>

          {result && (
            <div className="border border-slate-200 rounded-sm p-3 space-y-2" data-testid={`bulk-result-${kind}`}>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><CheckCircleIcon size={16} weight="fill" /> {result.created} created</span>
                {result.logins_created > 0 && (
                  <span className="inline-flex items-center gap-1 text-blue-700 font-bold">🔑 {result.logins_created} logins</span>
                )}
                {result.skipped > 0 && <span className="inline-flex items-center gap-1 text-amber-700 font-bold"><WarningCircleIcon size={16} weight="fill" /> {result.skipped} skipped</span>}
                {result.errors > 0 && <span className="inline-flex items-center gap-1 text-red-700 font-bold"><XCircleIcon size={16} weight="fill" /> {result.errors} errors</span>}
              </div>

              {(result.credentials || []).length > 0 && (
                <div className="rounded-sm border border-blue-200 bg-blue-50 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-[11px] uppercase tracking-widest text-blue-800 font-bold">/// Generated Logins ({result.credentials.length})</div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const rows = [["Name", "Email", "Password"], ...result.credentials.map((c) => [c.name, c.email, c.password])];
                        const csv = rows.map((r) => r.map((v) => `"${(v || "").replace(/"/g, '""')}"`).join(",")).join("\n");
                        const blob = new Blob([csv], { type: "text/csv" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `driver_logins_${new Date().toISOString().slice(0, 10)}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="h-7 rounded-sm text-xs"
                      data-testid="download-credentials-csv"
                    >
                      <DownloadIcon size={13} weight="bold" className="mr-1" /> Download CSV
                    </Button>
                  </div>
                  <div className="max-h-40 overflow-auto text-xs font-mono">
                    <table className="w-full">
                      <thead className="text-blue-700">
                        <tr>
                          <th className="text-left pr-2 pb-1">Name</th>
                          <th className="text-left pr-2 pb-1">Email</th>
                          <th className="text-left pb-1">Password</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-700">
                        {result.credentials.map((c, i) => (
                          <tr key={i} className="border-t border-blue-100">
                            <td className="pr-2 py-1">{c.name}</td>
                            <td className="pr-2 py-1">{c.email}</td>
                            <td className="py-1 font-bold text-blue-700">{c.password}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-blue-800 mt-2">Give drivers these credentials. You can also copy them any time from Drivers → three-dot menu.</p>
                </div>
              )}

              {(result.no_login_rows || []).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-mono text-slate-600">No login created ({result.no_login_rows.length})</summary>
                  <ul className="mt-1 pl-3 space-y-0.5 text-slate-600">
                    {result.no_login_rows.slice(0, 10).map((r, i) => (
                      <li key={i} className="font-mono">Row {r.row}: {r.name || "—"} · {r.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
              {(result.skipped_rows || []).length > 0 && (
                <details className="text-xs" open>
                  <summary className="cursor-pointer font-mono text-slate-600">Skipped ({result.skipped_rows.length})</summary>
                  <ul className="mt-1 pl-3 space-y-0.5 text-slate-600">
                    {result.skipped_rows.slice(0, 10).map((r, i) => (
                      <li key={i} className="font-mono">Row {r.row}: {r.unit_number || r.name || "—"} · {r.reason}</li>
                    ))}
                    {result.skipped_rows.length > 10 && <li className="italic text-slate-400">+ {result.skipped_rows.length - 10} more</li>}
                  </ul>
                </details>
              )}
              {(result.error_rows || []).length > 0 && (
                <details className="text-xs" open>
                  <summary className="cursor-pointer font-mono text-red-700">Errors ({result.error_rows.length})</summary>
                  <ul className="mt-1 pl-3 space-y-0.5 text-red-700">
                    {result.error_rows.slice(0, 10).map((r, i) => (
                      <li key={i} className="font-mono">Row {r.row}: {r.reason}</li>
                    ))}
                    {result.error_rows.length > 10 && <li className="italic text-slate-400">+ {result.error_rows.length - 10} more</li>}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button type="button" variant="outline" onClick={close} className="rounded-sm">Close</Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!file || uploading}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid={`bulk-submit-${kind}`}
          >
            {uploading ? "Importing..." : "Import file"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
